import { useRef } from 'react';

/*
 * CAMPO ÚNICO DE FECHA DEL NÚCLEO (RF-149, D-240, RN-400 a RN-406).
 *
 * POR QUÉ EXISTE: había CUATRO `input type="date"` sueltos y ninguna pieza
 * compartida —la captura de gasto/ingreso/devolución, la del traspaso y los dos
 * del rango del historial—. Arreglar el toque en cuatro sitios por separado es
 * garantizar que el quinto nazca mal (D-240, RNF-076). A partir de este
 * incremento ninguna pantalla declara un `input type="date"` propio.
 *
 * QUÉ RESUELVE Y QUÉ NO, dicho sin adornos:
 *   · Tocar CUALQUIER PARTE del campo abre el selector, no solo el icono del
 *     calendario (RN-401). Eso es todo lo que hacía falta: que tocar el texto
 *     enfocara sin abrir el calendario era conducta nativa del navegador, no
 *     código propio (CR-03).
 *   · EL CONTROL NATIVO NO SE SUSTITUYE (D-245, RN-404). El tecleo directo
 *     funciona donde el aparato lo permita; donde no —Android no ofrece escritura
 *     sobre este control—, es LIMITACIÓN CONOCIDA Y NO DEFECTO (RN-432). No se
 *     levanta como hallazgo y NO SE LE CUENTA A LA PERSONA: la pantalla no
 *     muestra aviso, leyenda ni explicación, porque contárselo sería justificar
 *     el diseño (RN-442).
 *   · Si el navegador no ofrece la orden de abrir el selector, el campo se
 *     comporta COMO HOY —el icono sigue abriéndolo— y no se muestra ningún error
 *     (RN-401).
 *   · Abrir el selector NO altera el valor: no lo cambia, no lo borra y no lo
 *     pone en hoy (RN-402). Aquí no se escribe el valor en ningún caso; lo único
 *     que se hace es pedirle al navegador que abra su selector.
 *
 * LAS REGLAS DE LA FECHA NO CAMBIAN NI UNA (RN-405): el tope superior lo sigue
 * poniendo quien usa el campo con `max`, la fecha futura sigue prohibida, la
 * fecha sigue siendo la LOCAL DEL DISPOSITIVO y el servidor valida igual. Este
 * componente es de CÓMO SE CAPTURA, no de QUÉ SE ACEPTA.
 */

/** Área táctil mínima (RN-406, RNF-082). No se baja de aquí. */
export const ALTO_MINIMO_DEL_CAMPO = 44;

export function CampoDeFecha({
  id,
  name,
  valor,
  alCambiar,
  max,
  min,
  style,
  datoDePrueba = 'campo-de-fecha',
  ...resto
}) {
  const referencia = useRef(null);

  /*
   * RN-401. `showPicker()` es la orden estándar para abrir el selector nativo.
   * Va en el `click` —un solo disparo por toque— y no en `focus`, que llegaría
   * también al enfocar con teclado y abriría el calendario sin que nadie lo
   * pidiera; con el campo ya enfocado, un segundo toque vuelve a pedir lo mismo y
   * el navegador no lo encadena (RN-402).
   */
  function abrirSelector() {
    const campo = referencia.current;
    if (!campo || typeof campo.showPicker !== 'function') return;
    try {
      campo.showPicker();
    } catch {
      // Navegador que no lo permite sin gesto propio, o selector ya abierto. Se
      // comporta como hoy y NO se muestra nada (RN-401, RN-432b).
    }
  }

  return (
    <input
      ref={referencia}
      id={id}
      name={name}
      type="date"
      value={valor ?? ''}
      max={max}
      min={min}
      data-prueba={datoDePrueba}
      onClick={abrirSelector}
      onChange={(evento) => alCambiar?.(evento.target.value)}
      style={{ minHeight: ALTO_MINIMO_DEL_CAMPO, minWidth: 0, ...style }}
      {...resto}
    />
  );
}

export default CampoDeFecha;
